import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { lastValueFrom } from 'rxjs';
import { FirmaActaEppService } from '../../services/firma-acta-epp.service';

interface TokenPayload {
  sub: string;
  idRelacionado: string;
  iat: number;
  exp: number;
}

interface ActaCompleta {
  id: number;
  numeroActa: string;
  fechaEntrega: Date;
  lugarEntrega: string;
  tipoEntrega: string;
  observaciones: string;
  estado: string;
  empleadoRecibe: {
    id: number;
    nombre: string;
    cargo: string;
    area: string;
    email: string;
  };
  empleadoEntrega: {
    id: number;
    nombre: string;
    cargo: string;
    area: string;
    email: string;
  };
  bodega: {
    id: number;
    nombre: string;
  };
  elementos: Array<{
    id: number;
    nombre: string;
    descripcion: string;
    cantidad: number;
    fichaTecnica: {
      titulo: string;
      certificado: string;
    };
  }>;
}

@Component({
  selector: 'app-firma-acta-epp',
  templateUrl: './firma-acta-epp.component.html',
  styleUrls: ['./firma-acta-epp.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    PanelModule,
    TableModule
  ],
  providers: [MessageService]
})
export class FirmaActaEppComponent implements OnInit {
  @ViewChild('firmaCanvas') firmaCanvas!: ElementRef<HTMLCanvasElement>;
  
  token: string | null = null;
  actaData: ActaCompleta | null = null;
  isDrawing = false;
  firmaForm: FormGroup;
  loading = false;
  tokenPayload: TokenPayload | null = null;
  yaFirmado = false; // Nueva propiedad para controlar si ya está firmado
  firmaExistente: string | null = null; // Propiedad para almacenar la firma existente

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private firmaActaEppService: FirmaActaEppService
  ) {
    this.firmaForm = this.fb.group({
      firma: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token');
    if (this.token) {
      this.validarToken();
    } else {
      this.mostrarError('Token no válido');
    }
  }

  private validarToken() {
    try {
      // Decodificar el token JWT (solo la parte del payload)
      const tokenParts = this.token!.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Token malformado');
      }

      const payload = JSON.parse(atob(tokenParts[1])) as TokenPayload;
      this.tokenPayload = payload;

      // Verificar si el token ha expirado
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        throw new Error('Token expirado');
      }

      console.log('Token válido, payload:', payload);
      this.cargarDatosActa();
    } catch (error) {
      console.error('Error validando token:', error);
      this.mostrarError('Token inválido o expirado');
    }
  }

  private async cargarDatosActa() {
    try {
      this.loading = true;

      const acta = await lastValueFrom(this.firmaActaEppService.obtenerActa(this.token!));

      this.actaData = {
        id: acta.id,
        numeroActa: acta.numeroActa,
        fechaEntrega: new Date(acta.fechaEntrega),
        lugarEntrega: acta.lugarEntrega,
        tipoEntrega: acta.tipoEntrega,
        observaciones: acta.observaciones || '',
        estado: acta.estado,
        empleadoRecibe: acta.empleadoRecibe,
        empleadoEntrega: acta.empleadoEntrega,
        bodega: acta.bodega,
        elementos: (acta.elementos || []).map((elemento: any) => ({
          id: elemento.id,
          nombre: elemento.nombre,
          descripcion: elemento.descripcion,
          cantidad: elemento.cantidad,
          fichaTecnica: {
            titulo: elemento.tituloFicha,
            certificado: 'N/A'
          }
        }))
      };

      this.yaFirmado = !!(acta.firmaRecibe && acta.firmaRecibe.trim() !== '');
      this.firmaExistente = acta.firmaRecibe;

      if (this.yaFirmado) {
        this.messageService.add({
          severity: 'info',
          summary: 'Acta ya firmada',
          detail: 'Este acta ya ha sido firmado anteriormente. Los elementos han sido entregados.'
        });
      }

      this.loading = false;

    } catch (error: any) {
      console.error('Error cargando datos del acta:', error);
      this.mostrarError(error?.error?.detalle || 'Error cargando los datos del acta');
      this.loading = false;
    }
  }


  private mostrarError(mensaje: string) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
    setTimeout(() => {
      this.router.navigate(['/']);
    }, 3000);
  }

  startDrawing(event: MouseEvent) {
    this.isDrawing = true;
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.beginPath();
      ctx.moveTo(event.clientX - rect.left, event.clientY - rect.top);
    }
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.lineTo(event.clientX - rect.left, event.clientY - rect.top);
      ctx.stroke();
    }
  }

  stopDrawing() {
    this.isDrawing = false;
  }

  limpiarFirma() {
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async guardarFirma() {
    try {
      if (!this.token || !this.actaData) {
        throw new Error('Datos incompletos');
      }

      const canvas = this.firmaCanvas.nativeElement;
      const firmaDataUrl = canvas.toDataURL();

      const respuesta = await lastValueFrom(this.firmaActaEppService.firmar(this.token, firmaDataUrl));

      (respuesta?.mensajes || []).forEach((mensaje: any) => this.messageService.add(mensaje));

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Firma guardada y stock actualizado correctamente. Los elementos han sido entregados.'
      });

      setTimeout(async () => {
        await this.cargarDatosActa();
      }, 1000);

    } catch (error: any) {
      console.error('Error guardando firma:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.detalle || 'Error guardando la firma'
      });
    }
  }

}
